import React, { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import AddIcon from '@atlaskit/icon/core/add';
import Button from '@atlaskit/button/new';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import Heading from '@atlaskit/heading';

import { fg } from '@confluence/feature-gating';

import { messages } from '../../messages.ts';
import type { FieldMetadata } from '../../__types__/apiUtils.ts';
import type { UnsavedColumnToFieldMapping } from '../../providers/BulkConfigureContextProvider.tsx';
import {
	useIsConfigureLoading,
	useMappingsActions,
	useMappingsState,
} from '../../providers/BulkConfigureContextProvider.tsx';
import { useIssueCreateSidePanelContext } from '../../providers/IssueCreateSidePanelContextProvider.tsx';
import { useBulkCreateLoggingEvents } from '../../utils/useBulkCreateLoggingEvents.tsx';

import { ColumnToIssueFieldMapping } from './ColumnToIssueFieldMapping.tsx';

const mapFieldsToOptions = (fields: FieldMetadata[]) => {
	return fields.map((field) => ({
		label: field.name,
		value: field,
	}));
};

type MappingValidationError = {
	message: string;
};

const createIssueFieldMappingKey = (mapping: UnsavedColumnToFieldMapping, index: number) => {
	return `${index}-${mapping.column?.index ?? 'null'}-${mapping.field?.fieldId ?? 'null'}`;
};

export const ColumnToIssueFieldMappingView = forwardRef<
	{ validateMappings: () => void },
	{ onAutoScrollOnMenuOpen?: () => void }
>(({ onAutoScrollOnMenuOpen }, ref) => {
	const { formatMessage } = useIntl();
	const [{ pageMode }] = useIssueCreateSidePanelContext();
	const { isLoading: isConfigureLoading } = useIsConfigureLoading();
	const {
		optionsColumns,
		optionsIssueTypeFields,
		unsavedColumnToFieldMappings,
		loadingIssueTypeFields,
	} = useMappingsState();
	const { setUnsavedColumnToFieldMappings, addUnsavedColumnToFieldMapping } = useMappingsActions();
	const tableColumnOptions = optionsColumns
		? optionsColumns.map((column) => ({ label: column.header, value: column }))
		: [];
	const issueFieldOptions = optionsIssueTypeFields
		? mapFieldsToOptions(optionsIssueTypeFields)
		: [];
	const isLoading = isConfigureLoading || loadingIssueTypeFields;
	const { logAddMappingClicked } = useBulkCreateLoggingEvents();

	// Get mappings with updated column references - compute this synchronously to avoid double renders
	const mappingsWithUpdatedColumns = useMemo(() => {
		if (!unsavedColumnToFieldMappings || !optionsColumns) return unsavedColumnToFieldMappings;

		// Create a map of column index to current column objects for efficient lookup
		const columnsMap = new Map(optionsColumns.map((col) => [col.index, col]));

		return unsavedColumnToFieldMappings.map((mapping) => {
			if (mapping.column) {
				const updatedColumn = columnsMap.get(mapping.column.index);
				if (updatedColumn) {
					return { ...mapping, column: updatedColumn };
				}
			}
			return mapping;
		});
	}, [unsavedColumnToFieldMappings, optionsColumns]);

	const handleMappingChange = useCallback(
		(index: number, newMapping: UnsavedColumnToFieldMapping) => {
			if (unsavedColumnToFieldMappings) {
				const updatedMappings = unsavedColumnToFieldMappings.map((mapping, idx) =>
					idx === index ? newMapping : mapping,
				);
				setUnsavedColumnToFieldMappings(updatedMappings);
			}
		},
		[unsavedColumnToFieldMappings, setUnsavedColumnToFieldMappings],
	);

	// Create a Set of mapped column indices for quick lookup
	const mappedColumnIndices = useMemo(() => {
		if (!mappingsWithUpdatedColumns) {
			return new Set<number>();
		}
		return new Set(
			mappingsWithUpdatedColumns
				.map((mapping) => mapping.column?.index)
				.filter((index) => index !== undefined) as number[],
		);
	}, [mappingsWithUpdatedColumns]);

	// Find the next available column that is not already mapped (or undefined if none exists)
	const nextAvailableColumn = useMemo(() => {
		if (!optionsColumns) {
			return undefined;
		}

		// Find the first unmapped column starting from index 0
		for (const column of optionsColumns) {
			if (!mappedColumnIndices.has(column.index)) {
				return column;
			}
		}

		return undefined; // No available column found
	}, [optionsColumns, mappedColumnIndices]);

	const addMapping = useCallback(() => {
		if (isLoading) {
			return;
		}
		const newMapping: UnsavedColumnToFieldMapping = {
			column: nextAvailableColumn,
			field: undefined,
			errors: {},
			isDefaultMapping: false,
		};
		addUnsavedColumnToFieldMapping(newMapping);
		onAutoScrollOnMenuOpen?.();
	}, [isLoading, addUnsavedColumnToFieldMapping, nextAvailableColumn, onAutoScrollOnMenuOpen]);

	const handleAddMappingClick = useCallback(() => {
		logAddMappingClicked();
		addMapping();
		onAutoScrollOnMenuOpen?.();
	}, [logAddMappingClicked, addMapping, onAutoScrollOnMenuOpen]);

	// TODO: Please update the messages and ensure they are internationalized once the content design has been finalized.
	const validateMappings = (): boolean => {
		if (!mappingsWithUpdatedColumns) {
			return false;
		}
		let isValid = true;
		const mappedField = new Set<string>();
		const updatedMappings = mappingsWithUpdatedColumns.map((mapping) => {
			const { column, field } = mapping;
			const errors: { column?: MappingValidationError; field?: MappingValidationError } = {};
			if (!column) {
				errors.column = {
					message: formatMessage(messages.bulkConfigureMapTableToIssueErrorColumnNotSelected),
				};
				isValid = false;
			}
			if (!field) {
				errors.field = {
					message: formatMessage(messages.bulkConfigureMapTableToIssueErrorFieldNotSelected),
				};
				isValid = false;
			} else {
				const fieldId = field.fieldId;
				if (mappedField.has(fieldId)) {
					errors.field = {
						message: formatMessage(messages.bulkConfigureMapTableToIssueErrorFieldAlreadyMapped),
					};
					isValid = false;
				} else {
					mappedField.add(fieldId);
				}
			}
			return {
				...mapping,
				errors,
			};
		});
		setUnsavedColumnToFieldMappings(updatedMappings);
		return isValid;
	};

	useImperativeHandle(ref, () => ({
		validateMappings,
	}));

	return (
		<Stack space="space.100">
			<Heading size="small">
				{fg('confluence-issue-terminology-refresh') ? (
					<FormattedMessage {...messages.bulkConfigureMapTableToIssueTitleIssueTermRefresh} />
				) : (
					<FormattedMessage {...messages.bulkConfigureMapTableToIssueTitle} />
				)}
			</Heading>
			<Text color="color.text.subtlest">
				{fg('confluence-issue-terminology-refresh') ? (
					<FormattedMessage {...messages.bulkConfigureMapTableToIssueDescriptionIssueTermRefresh} />
				) : (
					<FormattedMessage {...messages.bulkConfigureMapTableToIssueDescription} />
				)}
			</Text>
			<Box padding="space.100">
				<Stack space="space.100">
					<Stack space="space.300">
						{mappingsWithUpdatedColumns?.map((mapping, index) => {
							return (
								<ColumnToIssueFieldMapping
									key={createIssueFieldMappingKey(mapping, index)}
									index={index}
									mapping={mapping}
									tableColumnOptions={tableColumnOptions}
									issueFieldOptions={issueFieldOptions}
									onMappingChange={handleMappingChange}
									isLoading={loadingIssueTypeFields || isLoading}
									isDisabled={loadingIssueTypeFields || isLoading}
									pageMode={pageMode}
									onAutoScrollOnMenuOpen={onAutoScrollOnMenuOpen}
								/>
							);
						})}
					</Stack>
				</Stack>
			</Box>
			<Box padding="space.0" paddingBlockStart="space.025">
				<Stack alignInline="start">
					<Button
						appearance="subtle"
						iconBefore={(iconProps) => <AddIcon {...iconProps} />}
						onClick={handleAddMappingClick}
						isDisabled={loadingIssueTypeFields || isLoading}
					>
						<FormattedMessage {...messages.bulkConfigureMapTableToIssueAddMapping} />
					</Button>
				</Stack>
			</Box>
		</Stack>
	);
});
